import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
// import {
//   Popover,
//   PopoverContent,
//   PopoverTrigger,
// } from "@/components/ui/popover"; // Not using Popover anymore
import { format } from "date-fns"; // Using for date formatting in edit mode
// import { fr } from "date-fns/locale"; // Not using date formatting
import { Video, Phone, MapPin, Monitor } from "lucide-react";
import { cn } from "@/lib/utils";
// import { Calendar } from "@/components/ui/calendar"; // Not using custom calendar

const InterviewForm = ({
  interview,
  onSubmit,
  onCancel,
  candidates = [],
  interviewers = [],
}) => {
  const [formData, setFormData] = useState({
    candidature_id: "",
    type_entretien: "",
    date_entretien: "",
    time: "",
    lieu: "",
    duree: "60",
    intervieweur_id: "",
    notes: "",
    statut: "en_attente",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  // const [selectedDate, setSelectedDate] = useState(); // Not using custom calendar
  // const [isDatePickerOpen, setIsDatePickerOpen] = useState(false); // Not using custom calendar

  useEffect(() => {
    if (interview) {
      const interviewDate = new Date(interview.date_entretien);
      console.log(candidates);

      setFormData({
        candidature_id:
          interview.candidature_id || interview.candidature?.id || "",
        type_entretien: interview.type_entretien || "",
        date_entretien: interview.date_entretien
          ? format(interviewDate, "yyyy-MM-dd")
          : "",
        time: interview.date_entretien ? format(interviewDate, "HH:mm") : "",
        lieu: interview.lieu || "",
        duree: interview.duree?.toString() || "60",
        intervieweur_id: interview.intervieweur_id || "",
        notes: interview.notes || "",
        statut: interview.statut || "en_attente",
      });
      // setSelectedDate(interviewDate); // Not using custom calendar
    }
  }, [interview]);

  // Debug selectedDate changes - Not using custom calendar
  // useEffect(() => {
  //   console.log("selectedDate changed to:", selectedDate);
  // }, [selectedDate]);

  const handleChange = (field, value) => {
    console.log(`Updating field ${field} with value:`, value);
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };
      console.log("New form data:", newData);
      return newData;
    });

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Not using custom calendar - using HTML date input instead
  // const handleDateSelect = (date) => {
  //   console.log("Date selected:", date);
  //   if (date) {
  //     setSelectedDate(date);
  //     const formattedDate = format(date, "yyyy-MM-dd");
  //     console.log("Formatted date:", formattedDate);
  //     setFormData((prev) => ({
  //       ...prev,
  //       date_entretien: formattedDate,
  //     }));

  //     // Clear any date errors
  //     if (errors.date_entretien) {
  //       setErrors((prev) => ({
  //         ...prev,
  //         date_entretien: "",
  //       }));
  //     }

  //     // Close the popover
  //     setIsDatePickerOpen(false);
  //   }
  // };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.candidature_id) {
      newErrors.candidature_id = "Sélectionnez un candidat";
    }

    if (!formData.type_entretien) {
      newErrors.type_entretien = "Sélectionnez un type d'entretien";
    }

    if (!formData.date_entretien) {
      newErrors.date_entretien = "Sélectionnez une date";
    }

    if (!formData.time) {
      newErrors.time = "Sélectionnez une heure";
    }

    if (!formData.duree || parseInt(formData.duree) < 15) {
      newErrors.duree = "La durée doit être d'au moins 15 minutes";
    }

    // Validate location for in-person interviews
    if (formData.type_entretien === "presentiel" && !formData.lieu) {
      newErrors.lieu = "Le lieu est requis pour un entretien en présentiel";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Combine date and time
      const datetime = new Date(`${formData.date_entretien}T${formData.time}`);

      const submitData = {
        candidature_id: formData.candidature_id,
        type_entretien: formData.type_entretien,
        date_entretien: datetime.toISOString(),
        lieu: formData.lieu,
        duree: parseInt(formData.duree),
        intervieweur_id: formData.intervieweur_id || null,
        notes: formData.notes,
        ...(interview && { statut: formData.statut }),
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  const platformOptions = [
    {
      value: "zoom",
      label: "Zoom",
      icon: <Video className="h-4 w-4" />,
      description: "Réunion vidéo Zoom",
      color: "blue",
    },
    {
      value: "google_meet",
      label: "Google Meet",
      icon: <Video className="h-4 w-4" />,
      description: "Réunion Google Meet",
      color: "green",
    },
    {
      value: "discord",
      label: "Discord",
      icon: <Monitor className="h-4 w-4" />,
      description: "Appel Discord",
      color: "purple",
    },
    {
      value: "teams",
      label: "Microsoft Teams",
      icon: <Video className="h-4 w-4" />,
      description: "Réunion Teams",
      color: "blue",
    },
    {
      value: "telephonique",
      label: "Téléphonique",
      icon: <Phone className="h-4 w-4" />,
      description: "Entretien téléphonique",
      color: "gray",
    },
    {
      value: "presentiel",
      label: "Présentiel",
      icon: <MapPin className="h-4 w-4" />,
      description: "Entretien en personne",
      color: "orange",
    },
    {
      value: "technique",
      label: "Technique",
      icon: <Monitor className="h-4 w-4" />,
      description: "Test technique",
      color: "indigo",
    },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Candidate Selection */}
        <div className="md:col-span-2">
          <Label htmlFor="candidature_id">Candidat *</Label>
          <Select
            value={formData.candidature_id}
            onValueChange={(value) => handleChange("candidature_id", value)}
          >
            <SelectTrigger
              className={errors.candidature_id ? "border-red-500" : ""}
            >
              <SelectValue placeholder="Sélectionner un candidat" />
            </SelectTrigger>
            <SelectContent>
              {candidates.map((candidate) => (
                <SelectItem key={candidate.id} value={candidate.id}>
                  <div className="flex flex-col">
                    <span>
                      {candidate.nom} {candidate.prenom}
                    </span>
                    <span className="text-sm text-gray-500">
                      {candidate.offre?.titre} •{" "}
                      {candidate.offre?.departement?.nom}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.candidature_id && (
            <p className="text-sm text-red-500 mt-1">{errors.candidature_id}</p>
          )}
        </div>

        {/* Platform Selection */}
        <div className="md:col-span-2">
          <Label htmlFor="type_entretien">Type d'entretien *</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
            {platformOptions.map((platform) => (
              <div
                key={platform.value}
                className={cn(
                  "border rounded-lg p-3 cursor-pointer transition-all",
                  formData.type_entretien === platform.value
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                )}
                onClick={() => handleChange("type_entretien", platform.value)}
              >
                <div className="flex items-center gap-2 mb-1">
                  {platform.icon}
                  <span className="font-medium text-sm">{platform.label}</span>
                </div>
                <p className="text-xs text-gray-600">{platform.description}</p>
              </div>
            ))}
          </div>
          {errors.type_entretien && (
            <p className="text-sm text-red-500 mt-1">{errors.type_entretien}</p>
          )}
        </div>

        {/* Date Selection */}
        <div>
          <Label htmlFor="date_entretien">Date de l'entretien *</Label>
          <Input
            id="date_entretien"
            type="date"
            value={formData.date_entretien}
            onChange={(e) => handleChange("date_entretien", e.target.value)}
            min={new Date().toISOString().split("T")[0]}
            className={cn("w-full", errors.date_entretien && "border-red-500")}
          />
          {errors.date_entretien && (
            <p className="text-sm text-red-500 mt-1">{errors.date_entretien}</p>
          )}
        </div>

        {/* Time Selection */}
        <div>
          <Label htmlFor="time">Heure *</Label>
          <Input
            id="time"
            type="time"
            value={formData.time}
            onChange={(e) => handleChange("time", e.target.value)}
            className={errors.time ? "border-red-500" : ""}
          />
          {errors.time && (
            <p className="text-sm text-red-500 mt-1">{errors.time}</p>
          )}
        </div>

        {/* Duration */}
        <div>
          <Label htmlFor="duree">Durée (minutes) *</Label>
          <Select
            value={formData.duree}
            onValueChange={(value) => handleChange("duree", value)}
          >
            <SelectTrigger className={errors.duree ? "border-red-500" : ""}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15">15 minutes</SelectItem>
              <SelectItem value="30">30 minutes</SelectItem>
              <SelectItem value="45">45 minutes</SelectItem>
              <SelectItem value="60">1 heure</SelectItem>
              <SelectItem value="90">1h 30</SelectItem>
              <SelectItem value="120">2 heures</SelectItem>
              <SelectItem value="180">3 heures</SelectItem>
            </SelectContent>
          </Select>
          {errors.duree && (
            <p className="text-sm text-red-500 mt-1">{errors.duree}</p>
          )}
        </div>

        {/* Interviewer */}
        <div>
          <Label htmlFor="intervieweur_id">Intervieweur</Label>
          <Select
            value={formData.intervieweur_id}
            onValueChange={(value) => handleChange("intervieweur_id", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner un intervieweur" />
            </SelectTrigger>
            <SelectContent>
              {interviewers.map((interviewer) => (
                <SelectItem key={interviewer.id} value={interviewer.id}>
                  {interviewer.prenom} {interviewer.nom}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Location (for in-person interviews) */}
        {formData.type_entretien === "presentiel" && (
          <div className="md:col-span-2">
            <Label htmlFor="lieu">Lieu de l'entretien *</Label>
            <Input
              id="lieu"
              value={formData.lieu}
              onChange={(e) => handleChange("lieu", e.target.value)}
              placeholder="Adresse complète du lieu d'entretien"
              className={errors.lieu ? "border-red-500" : ""}
            />
            {errors.lieu && (
              <p className="text-sm text-red-500 mt-1">{errors.lieu}</p>
            )}
          </div>
        )}

        {/* Status (for editing) */}
        {interview && (
          <div>
            <Label htmlFor="statut">Statut</Label>
            <Select
              value={formData.statut}
              onValueChange={(value) => handleChange("statut", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en_attente">En attente</SelectItem>
                <SelectItem value="confirme">Confirmé</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="termine">Terminé</SelectItem>
                <SelectItem value="reporte">Reporté</SelectItem>
                <SelectItem value="annule">Annulé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Notes */}
        <div className="md:col-span-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            placeholder="Notes ou instructions spéciales pour l'entretien..."
            rows={3}
          />
        </div>
      </div>

      {/* Information Card */}
      {formData.type_entretien && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <div className="text-blue-600">
                {
                  platformOptions.find(
                    (p) => p.value === formData.type_entretien
                  )?.icon
                }
              </div>
              <div>
                <h4 className="font-medium text-blue-900">
                  {
                    platformOptions.find(
                      (p) => p.value === formData.type_entretien
                    )?.label
                  }
                </h4>
                <p className="text-sm text-blue-700">
                  {["zoom", "google_meet", "discord", "teams"].includes(
                    formData.type_entretien
                  )
                    ? "Un lien de réunion sera automatiquement généré après la création."
                    : formData.type_entretien === "telephonique"
                    ? "Assurez-vous d'avoir le numéro de téléphone du candidat."
                    : formData.type_entretien === "presentiel"
                    ? "N'oubliez pas de préciser l'adresse complète et les instructions d'accès."
                    : "Préparez l'environnement de test technique approprié."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" disabled={loading}>
          {loading
            ? "Enregistrement..."
            : interview
            ? "Mettre à jour"
            : "Créer l'entretien"}
        </Button>
      </div>
    </form>
  );
};

export default InterviewForm;
